import { useState, useRef, useMemo, useEffect, useContext, useCallback } from "react";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { CheckoutContext } from "@swiggy-private/checkout-core";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// types
import { CouponPageType, CouponListingActionPayload } from "@swiggy-private/checkout-core";
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// helpers
import { getCartId, getRestaurantId } from "@cart-client/helpers/Cart";
import { getPaymentOfferCoupons, getAppliedGiftingCoupons } from "@cart-client/helpers/CouponListing";
import { isEmpty } from "@swiggy-private/js-utils";
import { getAppliedRestaurantCouponCode } from "@swiggy-private/cart-ui";
import { getAppliedPaymentCouponCode } from "@swiggy-private/cart-ui";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

import { GiftingCartApiResponse, IAppliedCouponCodes } from "@swiggy-private/cart-types";

export const useCouponState = () => {
    const { cartState } = useContext(CartContext);
    const { checkoutState } = useContext(CheckoutContext);
    const { couponListingType } = (checkoutState.payload as CouponListingActionPayload) || {};

    const { reviewedCart: cartResponse } = cartState;
    const cartTotal = cartResponse?.data?.cart_total_shown_to_coupon || 0;
    const restaurantId = cartResponse ? getRestaurantId(cartResponse) : "";
    const cartId = getCartId(cartResponse as ReviewedCart);

    const [couponCode, setCouponCode] = useState<string>("");
    const [showCrouton, setShowCrouton] = useState(false);

    const isPaymentCouponListingPage = useRef<boolean>(couponListingType === CouponPageType.PAYMENT_COUPON_LISTING);
    const paymentOffersRef = useRef<HTMLDivElement>(null);

    const { data } = useContext(CartFlowContext);
    const isGiftingCouponListing = isGiftingCartSource(data?.cartSource);

    const {
        cards,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        coupons,
        nextBestCoupon,
        resetCouponListingPage,
        tncCoupon,
        hasInitiallyFetched,
    } = useContext(CouponListingContext);

    const paymentOfferCoupons = useMemo(() => !isEmpty(coupons) && getPaymentOfferCoupons(coupons), [coupons]);
    const hasPaymentOffersCoupon = !isEmpty(paymentOfferCoupons);
    const showBottomSheet = Boolean(isApplyingCoupon || errorMessage.trim() || isRemovingCoupon);

    const appliedGiftingCoupons: IAppliedCouponCodes[] | undefined = useMemo(() => {
        return getAppliedGiftingCoupons(cartResponse?.data as GiftingCartApiResponse);
    }, [cartResponse?.data]);

    const isCouponListingEmpty = useMemo(() => {
        return !isFetching && hasInitiallyFetched && isEmpty(coupons);
    }, [coupons, isFetching, hasInitiallyFetched]);

    const reloadCouponListing = useCallback(() => {
        setCouponCode("");
        resetCouponListingPage();

        const appliedCouponCode = isPaymentCouponListingPage.current
            ? getAppliedPaymentCouponCode(cartResponse?.data)
            : getAppliedRestaurantCouponCode(cartResponse?.data);

        fetchCoupons({
            restaurantId,
            cartValue: cartTotal,
            couponCode: appliedCouponCode,
            appliedCouponCodes: appliedGiftingCoupons,
            subpage: couponListingType,
        });
    }, [
        resetCouponListingPage,
        cartResponse?.data,
        fetchCoupons,
        restaurantId,
        cartTotal,
        appliedGiftingCoupons,
        couponListingType,
    ]);

    useEffect(() => {
        setShowCrouton(hasPaymentOffersCoupon);
    }, [hasPaymentOffersCoupon]);

    return {
        cartResponse,
        cartTotal,
        restaurantId,
        cartId,
        couponCode,
        setCouponCode,
        showCrouton,
        setShowCrouton,
        isPaymentCouponListingPage,
        paymentOffersRef,
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        coupons,
        nextBestCoupon,
        reloadCouponListing,
        tncCoupon,
        paymentOfferCoupons,
        showBottomSheet,
        isCouponListingEmpty,
        isGiftingCouponListing,
        cards,
        appliedGiftingCoupons,
    };
};
